import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Briefcase, ChevronDown, Clock3, Heart, Plus, X } from 'lucide-react';
import { Button } from '../../common';
import { Input } from '../../common';
import { Modal } from '../../common';
import { SelectDropdown, type SelectOption } from '../../common';
import { useChatStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import {
  JOB_ROLE_GROUPS,
  INTEREST_OPTIONS,
  MAX_CUSTOM_INTEREST_LENGTH,
  MAX_INTERESTS,
  MAX_INTEREST_OPTION_COUNT,
  getJobRoleLabel,
  getJobRoleCategoryLabel,
  getInterestDisplayLabel,
  isPresetInterestValue,
  mergeCustomInterestLabels,
  normalizeInterestKeyword,
  sanitizeInterestKeyword,
} from '@auw/i18n';
import {
  ActionRow,
  Divider,
  ErrorMsg,
  SectionBlock,
  SectionDesc,
  SectionHeader,
  SectionTitle,
} from '../styles';

/* ------------------------------------------------------------------ */
/* Styled components                                                   */
/* ------------------------------------------------------------------ */

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgSecondary};
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:has([data-open='true']),
  &:has([data-dropdown-open='true']) {
    z-index: 20;
  }
`;

const WideCard = styled(Card)`
  grid-column: 1 / -1;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const CardHeadMain = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const CardIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.accent};
  background: ${({ theme }) => theme.colors.accentLight};
`;

const CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CardLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CardHint = styled.span`
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const InterestAddButton = styled.button`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: transform ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme }) => theme.colors.border};
  }
`;

/* ---- Job Role Dropdown ---- */

const DropdownWrap = styled.div<{ $open: boolean }>`
  position: relative;
  width: 100%;
  z-index: ${({ $open }) => ($open ? 80 : 1)};
`;

const DropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.border};
  }
`;

const TriggerText = styled.span<{ $placeholder?: boolean }>`
  flex: 1;
  text-align: left;
  color: ${({ $placeholder, theme }) =>
    $placeholder ? theme.colors.textMuted : theme.colors.textPrimary};
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  max-height: 260px;
  overflow-y: auto;
  z-index: 50;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(32, 32, 34, 0.98)' : 'rgba(255, 255, 255, 0.98)'};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 6px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const GroupLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 8px 10px 4px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const RoleItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.accentLight};
      font-weight: 600;
    `}
`;

const CustomInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0 2px;
`;

/* ---- Interests Chips ---- */

const InterestsWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
`;

const ChipsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  align-items: stretch;
`;

const InterestChip = styled.button<{ $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  height: 100%;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
  word-break: break-word;
  cursor: pointer;
  border: 1.5px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.textPrimary};
    transform: translateY(-1px);
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      background: ${theme.colors.accentLight};
      border-color: ${theme.colors.accent};
      color: ${theme.colors.accent};
      font-weight: 600;
    `}

  ${({ $disabled, $selected }) =>
    $disabled &&
    !$selected &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
      &:hover {
        transform: none;
      }
    `}
`;

const CountHint = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const KeywordModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const KeywordModalDesc = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const KeywordMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const KeywordMetaText = styled.span<{ $error?: boolean }>`
  flex: 1;
  font-size: 0.75rem;
  color: ${({ theme, $error }) => ($error ? theme.colors.error : theme.colors.textMuted)};
`;

const KeywordCount = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const KeywordModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const FeedScheduleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeedScheduleDropdown = styled(SelectDropdown)`
  width: 100%;

  button[aria-haspopup='listbox'] {
    min-height: 48px;
    padding: 0 14px;
    border-radius: 12px;
    font-size: 0.875rem;
  }

  [role='listbox'] {
    margin-top: 6px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(32, 32, 34, 0.98)' : 'rgba(255, 255, 255, 0.98)'};
  }
`;

const FeedScheduleHint = styled.span`
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const AutoSaveRow = styled(ActionRow)`
  justify-content: space-between;
  align-items: center;
`;

const AutoSaveStatus = styled.span<{ $tone: 'default' | 'success' | 'error' }>`
  margin-right: auto;
  font-size: 0.75rem;
  color: ${({ theme, $tone }) => {
    if ($tone === 'error') return theme.colors.error;
    if ($tone === 'success') return theme.colors.success;
    return theme.colors.textMuted;
  }};
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const DEFAULT_FEED_DELIVERY_HOUR = 9;
const FEED_DELIVERY_HOURS = Array.from({ length: 24 }, (_, hour) => hour);

function formatFeedHourLabel(hour: number, tFn: (key: string, opts?: Record<string, unknown>) => string): string {
  const meridiem = hour < 12 ? tFn('settings:jobInterests.am') : tFn('settings:jobInterests.pm');
  const hour12 = hour % 12 || 12;
  return tFn('settings:jobInterests.hourFormat', { meridiem, hour: hour12 });
}

function formatLockedUntilLabel(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('ko-KR', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error) || !error.message) {
    return fallback;
  }

  const raw = error.message.replace(/^HTTP\s+\d+:\s*/i, '').trim();
  if (!raw) return fallback;

  try {
    const parsed = JSON.parse(raw) as { detail?: unknown };
    if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
      return parsed.detail.trim();
    }
  } catch {
    // ignore json parse errors
  }

  return raw;
}

export const JobRoleInterestsSection: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const user = useUserStore((s) => s.user);
  const updateProfile = useUserStore((s) => s.updateProfile);
  const addToast = useChatStore((s) => s.addToast);

  const persistedJobRole = user?.jobRole ?? null;
  const persistedInterests = useMemo(() => user?.interests ?? [], [user?.interests]);
  const persistedCustomInterests = useMemo(
    () => mergeCustomInterestLabels(user?.customInterests ?? [], user?.interests ?? []),
    [user?.customInterests, user?.interests],
  );
  const persistedFeedDeliveryHour = user?.feedDeliveryHour ?? DEFAULT_FEED_DELIVERY_HOUR;
  const persistedFeedDeliveryHourLockedUntil = user?.feedDeliveryHourLockedUntil ?? null;
  const feedDeliveryHourOptions = useMemo<SelectOption[]>(
    () => FEED_DELIVERY_HOURS.map((hour) => ({
      value: String(hour),
      label: `${hour.toString().padStart(2, '0')}:00`,
      description: t('settings:jobInterests.feedHourDesc', { time: formatFeedHourLabel(hour, t) }),
    })),
    [t],
  );

  const [jobRole, setJobRole] = useState<string | null>(persistedJobRole);
  const [customRole, setCustomRole] = useState('');
  const [interests, setInterests] = useState<string[]>(persistedInterests);
  const [customInterests, setCustomInterests] = useState<string[]>(persistedCustomInterests);
  const [feedDeliveryHour, setFeedDeliveryHour] = useState(persistedFeedDeliveryHour);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [lastSavedChangeType, setLastSavedChangeType] = useState<
    'content' | 'schedule' | 'mixed' | 'default'
  >('default');
  const [customInterestModalOpen, setCustomInterestModalOpen] = useState(false);
  const [customInterestDraft, setCustomInterestDraft] = useState('');
  const [customInterestDraftError, setCustomInterestDraftError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const savedStatusTimerRef = useRef<number | null>(null);
  const customInterestInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (persistedJobRole?.startsWith('custom:')) {
      setJobRole('custom');
      setCustomRole(persistedJobRole.slice(7));
    } else {
      setJobRole(persistedJobRole);
      setCustomRole('');
    }
    setInterests(persistedInterests);
    setCustomInterests(persistedCustomInterests);
    setFeedDeliveryHour(persistedFeedDeliveryHour);
    setSaveError(null);
    setSaveState('idle');
    setCustomInterestModalOpen(false);
    setCustomInterestDraft('');
    setCustomInterestDraftError(null);
  }, [
    persistedCustomInterests,
    persistedFeedDeliveryHour,
    persistedInterests,
    persistedJobRole,
    user?.userId,
  ]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => () => {
    if (savedStatusTimerRef.current !== null) {
      window.clearTimeout(savedStatusTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!customInterestModalOpen) return undefined;
    const timer = window.setTimeout(() => {
      customInterestInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [customInterestModalOpen]);

  const isCustom = jobRole === 'custom';
  const effectiveJobRole = useMemo(() => {
    if (!isCustom) return jobRole;
    const trimmed = customRole.trim();
    return trimmed ? `custom:${trimmed}` : null;
  }, [customRole, isCustom, jobRole]);

  const hasPendingCustomRole = isCustom && !customRole.trim();
  const interestSelectionInvalid = interests.length !== MAX_INTERESTS;
  const interestOptions = useMemo(
    () => {
      const options: Array<{ value: string; label: string }> = [];
      const seen = new Set<string>();

      customInterests.forEach((interest) => {
        const label = sanitizeInterestKeyword(interest);
        const value = normalizeInterestKeyword(label);
        if (!label || !value || isPresetInterestValue(value) || seen.has(value)) {
          return;
        }
        seen.add(value);
        options.push({ value, label });
      });

      INTEREST_OPTIONS.forEach((option) => {
        if (seen.has(option.value)) return;
        seen.add(option.value);
        options.push({ value: option.value, label: option.label });
      });

      return options;
    },
    [customInterests],
  );
  const feedDeliveryHourLockedUntilMs = useMemo(() => {
    if (!persistedFeedDeliveryHourLockedUntil) return null;
    const parsed = new Date(persistedFeedDeliveryHourLockedUntil).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }, [persistedFeedDeliveryHourLockedUntil]);
  const isFeedDeliveryHourLocked = Boolean(
    feedDeliveryHourLockedUntilMs !== null && feedDeliveryHourLockedUntilMs > Date.now(),
  );
  const feedDeliveryHourLockedUntilLabel = useMemo(
    () => formatLockedUntilLabel(persistedFeedDeliveryHourLockedUntil),
    [persistedFeedDeliveryHourLockedUntil],
  );

  const roleChanged = effectiveJobRole !== persistedJobRole;
  const interestsChanged =
    interests.length !== persistedInterests.length ||
    interests.some((v, i) => v !== persistedInterests[i]);
  const customInterestsChanged =
    customInterests.length !== persistedCustomInterests.length ||
    customInterests.some((value, index) => value !== persistedCustomInterests[index]);
  const contentChanged = roleChanged || interestsChanged || customInterestsChanged;
  const feedHourChanged = feedDeliveryHour !== persistedFeedDeliveryHour;
  const isDirty = contentChanged || feedHourChanged;

  const validationMessage = (() => {
    if (hasPendingCustomRole) return t('settings:jobInterests.enterJobRole');
    if (interestSelectionInvalid) return t('settings:jobInterests.interestRequired', { count: MAX_INTERESTS });
    return null;
  })();

  const handleSelectRole = (value: string) => {
    setJobRole(value);
    if (value !== 'custom') {
      setCustomRole('');
      setDropdownOpen(false);
    }
    setSaveError(null);
    setSaveState('idle');
  };

  const handleToggleInterest = (value: string) => {
    const normalizedValue = normalizeInterestKeyword(value);
    setInterests((prev) => {
      if (prev.includes(normalizedValue)) {
        return prev.filter((v) => v !== normalizedValue);
      }
      if (prev.length >= MAX_INTERESTS) return prev;
      return [...prev, normalizedValue];
    });
    setSaveError(null);
    setSaveState('idle');
  };

  const validateCustomInterestDraft = (value: string): string | null => {
    const label = sanitizeInterestKeyword(value);
    if (!label) return t('settings:jobInterests.customInterestEmpty');
    if (label.length > MAX_CUSTOM_INTEREST_LENGTH) {
      return t('settings:jobInterests.customInterestTooLong', { max: MAX_CUSTOM_INTEREST_LENGTH });
    }
    if (interestOptions.length >= MAX_INTEREST_OPTION_COUNT) {
      return t('settings:jobInterests.customInterestMaxCards', { max: MAX_INTEREST_OPTION_COUNT });
    }
    const normalized = normalizeInterestKeyword(label);
    if (interestOptions.some((option) => option.value === normalized)) {
      return t('settings:jobInterests.customInterestDuplicate');
    }
    return null;
  };

  const handleCloseCustomInterestModal = () => {
    setCustomInterestModalOpen(false);
    setCustomInterestDraft('');
    setCustomInterestDraftError(null);
  };

  const handleOpenCustomInterestModal = () => {
    if (interestOptions.length >= MAX_INTEREST_OPTION_COUNT) {
      addToast({
        type: 'warning',
        title: t('settings:jobInterests.customInterestMaxCards', { max: MAX_INTEREST_OPTION_COUNT }),
      });
      return;
    }
    setCustomInterestDraft('');
    setCustomInterestDraftError(null);
    setCustomInterestModalOpen(true);
  };

  const handleConfirmCustomInterest = () => {
    const nextLabel = sanitizeInterestKeyword(customInterestDraft);
    const nextError = validateCustomInterestDraft(nextLabel);
    if (nextError) {
      setCustomInterestDraftError(nextError);
      return;
    }
    setCustomInterests((prev) => [...prev, nextLabel]);
    setSaveError(null);
    setSaveState('idle');
    handleCloseCustomInterestModal();
  };

  const handleSaveChanges = async () => {
    if (!user?.userId || !isDirty || saving || validationMessage) return;

    const nextSavedChangeType: 'content' | 'schedule' | 'mixed' | 'default' =
      contentChanged && feedHourChanged
        ? 'mixed'
        : contentChanged
          ? 'content'
          : feedHourChanged
            ? 'schedule'
            : 'default';

    setSaving(true);
    setSaveError(null);
    setSaveState('saving');
    try {
      await updateProfile({
        job_role: effectiveJobRole,
        interests,
        custom_interests: customInterests,
        feed_delivery_hour: feedDeliveryHour,
      });
      setLastSavedChangeType(nextSavedChangeType);
      setSaveState('saved');
      if (savedStatusTimerRef.current !== null) {
        window.clearTimeout(savedStatusTimerRef.current);
      }
      savedStatusTimerRef.current = window.setTimeout(() => {
        setSaveState((prev) => (prev === 'saved' ? 'idle' : prev));
      }, 1800);
    } catch (error) {
      setSaveError(extractApiErrorMessage(error, t('settings:jobInterests.saveFail')));
      setSaveState('idle');
    } finally {
      setSaving(false);
    }
  };

  const displayJobRole = (() => {
    if (!jobRole) return null;
    if (jobRole === 'custom') return customRole.trim() || null;
    return getJobRoleLabel(jobRole);
  })();

  const displayCategory = jobRole && !jobRole.startsWith('custom:') && jobRole !== 'custom'
    ? getJobRoleCategoryLabel(jobRole)
    : null;

  const statusTone: 'default' | 'success' | 'error' =
    validationMessage
      ? 'error'
      : saveState === 'saved' && !isDirty
        ? 'success'
        : 'default';

  const statusMessage = validationMessage
    || (saving
      ? t('settings:jobInterests.saving')
      : isDirty
        ? t('settings:jobInterests.hasChanges')
        : saveState === 'saved'
          ? lastSavedChangeType === 'content'
            ? t('settings:jobInterests.savedContent')
            : lastSavedChangeType === 'schedule'
              ? t('settings:jobInterests.savedSchedule')
              : lastSavedChangeType === 'mixed'
                ? t('settings:jobInterests.savedMixed')
                : t('settings:jobInterests.saved')
          : '');

  return (
    <SectionBlock>
      <SectionHeader>
        <SectionTitle>{t('settings:jobInterests.title')}</SectionTitle>
      </SectionHeader>
      <SectionDesc>{t('settings:jobInterests.description')}</SectionDesc>
      <Divider />

      <CardGrid>
        {/* ---- 직군 선택 ---- */}
        <Card>
          <CardHead>
            <CardHeadMain>
              <CardIcon>
                <Briefcase size={18} />
              </CardIcon>
              <CardMeta>
                <CardLabel>{t('settings:jobInterests.jobRole')}</CardLabel>
              </CardMeta>
            </CardHeadMain>
          </CardHead>

          <DropdownWrap
            ref={dropdownRef}
            $open={dropdownOpen}
            data-dropdown-open={dropdownOpen ? 'true' : 'false'}
          >
            <DropdownTrigger
              type="button"
              onClick={() => setDropdownOpen((p) => !p)}
            >
              <TriggerText $placeholder={!displayJobRole}>
                {displayJobRole
                  ? `${displayCategory ? `${displayCategory} · ` : ''}${displayJobRole}`
                  : t('settings:jobInterests.selectJobRole')}
              </TriggerText>
              <ChevronDown size={16} style={{ flexShrink: 0, opacity: 0.5 }} />
            </DropdownTrigger>

            {dropdownOpen && (
              <DropdownPanel>
                {jobRole && (
                  <RoleItem
                    $active={false}
                    onClick={() => {
                      setJobRole(null);
                      setCustomRole('');
                      setDropdownOpen(false);
                      setSaveError(null);
                    }}
                  >
                    <X size={14} style={{ marginRight: 6, opacity: 0.5 }} />
                    {t('settings:jobInterests.clearSelection')}
                  </RoleItem>
                )}
                {JOB_ROLE_GROUPS.map((group) => (
                  <React.Fragment key={group.category}>
                    <GroupLabel>{group.category}</GroupLabel>
                    {group.roles.map((role) => (
                      <RoleItem
                        key={role.value}
                        $active={jobRole === role.value}
                        onClick={() => handleSelectRole(role.value)}
                      >
                        {role.label}
                      </RoleItem>
                    ))}
                  </React.Fragment>
                ))}

                {isCustom && (
                  <CustomInputRow>
                    <Input
                      value={customRole}
                      onChange={(e) => {
                        setCustomRole(e.target.value);
                        setSaveError(null);
                        setSaveState('idle');
                      }}
                      inputSize="sm"
                      placeholder={t('settings:jobInterests.customRolePlaceholder')}
                      autoFocus
                      style={{ flex: 1 }}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setDropdownOpen(false)}
                      disabled={!customRole.trim()}
                    >
                      {t('common:button.confirm')}
                    </Button>
                  </CustomInputRow>
                )}
              </DropdownPanel>
            )}
          </DropdownWrap>
        </Card>

        {/* ---- 관심사 선택 ---- */}
        <Card>
          <CardHead>
            <CardHeadMain>
              <CardIcon>
                <Heart size={18} />
              </CardIcon>
              <CardMeta>
                <CardLabel>{t('settings:jobInterests.interests')}</CardLabel>
              </CardMeta>
            </CardHeadMain>
            <InterestAddButton
              type="button"
              onClick={handleOpenCustomInterestModal}
              aria-label={t('settings:jobInterests.addInterestLabel')}
              title={t('settings:jobInterests.addInterestLabel')}
            >
              <Plus size={16} />
            </InterestAddButton>
          </CardHead>

          <InterestsWrap>
            <ChipsGrid>
              {interestOptions.map((opt) => {
                const selected = interests.includes(opt.value);
                const maxReached = interests.length >= MAX_INTERESTS;
                return (
                  <InterestChip
                    key={opt.value}
                    type="button"
                    $selected={selected}
                    $disabled={maxReached && !selected}
                    disabled={maxReached && !selected}
                    onClick={() => handleToggleInterest(opt.value)}
                  >
                    {getInterestDisplayLabel(opt.value, customInterests)}
                  </InterestChip>
                );
              })}
            </ChipsGrid>
            <CountHint>{t('settings:jobInterests.interestCount', { count: MAX_INTERESTS })}</CountHint>
          </InterestsWrap>
        </Card>

        <WideCard>
          <CardHead>
            <CardHeadMain>
              <CardIcon>
                <Clock3 size={18} />
              </CardIcon>
              <CardMeta>
                <CardLabel>{t('settings:jobInterests.feed')}</CardLabel>
                <CardHint>{t('settings:jobInterests.feedHint')}</CardHint>
              </CardMeta>
            </CardHeadMain>
          </CardHead>

          <FeedScheduleWrap>
            <FeedScheduleDropdown
              options={feedDeliveryHourOptions}
              value={String(feedDeliveryHour)}
              disabled={isFeedDeliveryHourLocked}
              onChange={(value) => {
                if (isFeedDeliveryHourLocked) return;
                setFeedDeliveryHour(Number(value));
                setSaveError(null);
                setSaveState('idle');
              }}
              placeholder={t('settings:jobInterests.feedTimePlaceholder')}
            />
            <FeedScheduleHint>
              {isFeedDeliveryHourLocked && feedDeliveryHourLockedUntilLabel
                ? t('settings:jobInterests.feedLocked', { date: feedDeliveryHourLockedUntilLabel })
                : t('settings:jobInterests.feedPrepareDaily', { time: formatFeedHourLabel(feedDeliveryHour, t) })}
            </FeedScheduleHint>
          </FeedScheduleWrap>
        </WideCard>
      </CardGrid>

      {saveError && <ErrorMsg>{saveError}</ErrorMsg>}

      <AutoSaveRow>
        <AutoSaveStatus $tone={statusTone}>{statusMessage}</AutoSaveStatus>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSaveChanges}
          disabled={saving || !isDirty || !!validationMessage}
        >
          {saving ? t('settings:jobInterests.saving') : t('settings:jobInterests.saveButton')}
        </Button>
      </AutoSaveRow>

      <Modal
        open={customInterestModalOpen}
        onClose={handleCloseCustomInterestModal}
        title={t('settings:jobInterests.customInterestTitle')}
        width="420px"
      >
        <KeywordModalBody>
          <KeywordModalDesc>
            {t('settings:jobInterests.customInterestDesc', { max: MAX_CUSTOM_INTEREST_LENGTH })}
          </KeywordModalDesc>
          <Input
            ref={customInterestInputRef}
            value={customInterestDraft}
            onChange={(e) => {
              setCustomInterestDraft(e.target.value);
              setCustomInterestDraftError(null);
            }}
            placeholder={t('settings:jobInterests.customInterestPlaceholder')}
            inputSize="md"
            maxLength={MAX_CUSTOM_INTEREST_LENGTH}
            error={!!customInterestDraftError}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirmCustomInterest();
              }
            }}
          />
          <KeywordMetaRow>
            <KeywordMetaText $error={!!customInterestDraftError}>
              {customInterestDraftError ?? t('settings:jobInterests.customInterestMaxCards', { max: MAX_INTEREST_OPTION_COUNT })}
            </KeywordMetaText>
            <KeywordCount>
              {sanitizeInterestKeyword(customInterestDraft).length}/{MAX_CUSTOM_INTEREST_LENGTH}
            </KeywordCount>
          </KeywordMetaRow>
          <KeywordModalActions>
            <Button variant="secondary" size="sm" onClick={handleCloseCustomInterestModal}>
              {t('common:button.cancel')}
            </Button>
            <Button variant="primary" size="sm" onClick={handleConfirmCustomInterest}>
              {t('common:button.confirm')}
            </Button>
          </KeywordModalActions>
        </KeywordModalBody>
      </Modal>
    </SectionBlock>
  );
};
